import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { formatDateLocal, addDaysToDateString } from '../utils/dateUtils';

/**
 * Visual calendar picker for selecting date and time from available service slots
 * Shows a monthly calendar view with available/unavailable days
 * Then shows available time slots for selected date
 */

const ServiceCalendarPicker = ({ service, selectedDate, selectedTime, onDateSelect, onTimeSelect, hideTimeSlots = false }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableDates, setAvailableDates] = useState(new Set());
  const [availableTimesForDate, setAvailableTimesForDate] = useState([]);
  const [showingTimeSlots, setShowingTimeSlots] = useState(false);

  useEffect(() => {
    calculateAvailableDates();
  }, [service, currentMonth]);

  useEffect(() => {
    if (selectedDate) {
      calculateAvailableTimesForDate(selectedDate);
    }
  }, [selectedDate, service]);

  const calculateAvailableDates = () => {
    if (!service || !service.availableTimeSlots) return;

    const dates = new Set();
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Get available days of week from service
    const availableDays = new Set();
    service.availableTimeSlots.forEach(slot => {
      (slot.days || []).forEach(day => availableDays.add(day.toLowerCase()));
    });

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check each day in the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayName = dayNames[date.getDay()];
      
      // Date must be today or in future AND on an available day of week
      if (date >= today && availableDays.has(dayName)) {
        dates.add(formatDateLocal(date));
      }
    }

    setAvailableDates(dates);
  };

  const calculateAvailableTimesForDate = (dateStr) => {
    if (!service || !service.availableTimeSlots) return;

    const date = new Date(dateStr + 'T00:00:00');
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[date.getDay()];

    // Get all time slots available on this day of week
    const times = [];
    service.availableTimeSlots.forEach(slot => {
      if (slot.days && slot.days.map(d => d.toLowerCase()).includes(dayName)) {
        times.push(slot.time);
      }
    });

    // Sort times chronologically
    times.sort((a, b) => {
      const [aHour, aMin] = a.split(':').map(Number);
      const [bHour, bMin] = b.split(':').map(Number);
      return (aHour * 60 + aMin) - (bHour * 60 + bMin);
    });

    setAvailableTimesForDate(times);
  };

  const changeMonth = (delta) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + delta, 1));
  };

  const changeDay = (delta) => {
    const currentDate = new Date(selectedDate + 'T00:00:00');
    currentDate.setDate(currentDate.getDate() + delta);
    const newDateStr = formatDateLocal(currentDate);
    
    // Only change if the new date is available
    if (availableDates.has(newDateStr)) {
      onDateSelect(newDateStr);
    }
  };

  const goBackToCalendar = () => {
    setShowingTimeSlots(false);
  };

  const selectDateAndShowTimes = (dateStr) => {
    onDateSelect(dateStr);
    setShowingTimeSlots(true);
  };

  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const days = [];
    
    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-12"></div>);
    }

    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = formatDateLocal(date);
      const isToday = date.toDateString() === today.toDateString();
      const isAvailable = availableDates.has(dateStr);
      const isSelected = dateStr === selectedDate;

      days.push(
        <button
          key={day}
          type="button"
          onClick={() => isAvailable && selectDateAndShowTimes(dateStr)}
          disabled={!isAvailable}
          className={`
            aspect-square rounded-lg text-sm font-medium transition-all relative
            ${isAvailable && !isSelected ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer' : ''}
            ${!isAvailable ? 'text-gray-300 cursor-not-allowed bg-gray-50' : ''}
            ${isSelected ? 'bg-blue-800 text-white ring-2 ring-blue-400' : ''}
          `}
        >
          {day}
          {isToday && (
            <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-white rounded-full"></div>
          )}
        </button>
      );
    }

    return (
      <div className="space-y-3">
        {/* Calendar Header */}
        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => changeMonth(-1)}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-base font-semibold">
            {monthNames[month]} {year}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => changeMonth(1)}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1">
          {dayHeaders.map(day => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 pb-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-1">
          {days}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-3 text-xs text-gray-600 pt-2 border-t">
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded bg-blue-600"></div>
            <span>Available</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded bg-blue-800 ring-1 ring-blue-400"></div>
            <span>Selected</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded bg-blue-600 relative">
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0.5 h-0.5 bg-white rounded-full"></div>
            </div>
            <span>Today</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {!showingTimeSlots || hideTimeSlots ? (
        // Calendar View
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Select Date</h4>
          {renderCalendar()}
        </div>
      ) : (
        // Time Slots View
        <div className="space-y-4">
          {/* Back to Calendar + Date Navigation */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={goBackToCalendar}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to calendar
            </button>
            
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => changeDay(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h3 className="text-lg font-semibold">
                {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { 
                  weekday: 'short', 
                  month: 'short', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}
              </h3>
              <button
                type="button"
                onClick={() => changeDay(1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Time Slots */}
          {availableTimesForDate.length > 0 ? (
            <div className="grid grid-cols-3 gap-3">
              {availableTimesForDate.map(time => (
                <button
                  key={time}
                  type="button"
                  onClick={() => onTimeSelect(time)}
                  className={`
                    px-4 py-3 rounded-lg text-sm font-medium transition-all
                    ${selectedTime === time 
                      ? 'bg-blue-600 text-white ring-2 ring-blue-400' 
                      : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-blue-400 hover:bg-blue-50'}
                  `}
                >
                  {formatTime(time)}
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-500">No time slots available for this date</p>
              <button
                type="button"
                onClick={goBackToCalendar}
                className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Choose a different date
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ServiceCalendarPicker;
